/**
 * Holds back add to cart until every required pick on the kit is made.
 *
 * Palettes for two and scents start empty (the solo palette is a real variant,
 * so one is always selected; varnish starts on the default finish), so two
 * kinds of element can be incomplete:
 *
 * - `<craftly-qty-picker>` — the steppers (palettes for two, scents, varnish
 *   for two). Defined in `craftly-qty-picker.js`.
 * - `<craftly-required-group>` — a plain radio group that needs one answer
 *   (solo varnish, solo Standartinis scent). Defined here.
 *
 * Both expose `isComplete` and `flag()`, and carry `data-incomplete` while
 * unfinished — the attribute is what CSS greys the button out with, the
 * getter is what the click is actually checked against.
 *
 * Loaded as `@theme/craftly-pick-guard` and by plain script tag from the
 * blocks; both resolve to the same URL, so this runs once per page.
 */

const PICKERS = 'craftly-qty-picker, craftly-required-group';

/**
 * @typedef {HTMLElement & { isComplete?: boolean, flag?: () => void }} Pick
 */

/**
 * @param {ParentNode} scope
 * @returns {Pick | undefined}
 */
export function firstIncomplete(scope) {
  return /** @type {Pick[]} */ ([...scope.querySelectorAll(PICKERS)]).find((pick) =>
    typeof pick.isComplete === 'boolean' ? !pick.isComplete : pick.hasAttribute('data-incomplete')
  );
}

/**
 * Mirrors the section's completeness onto its add to cart buttons for
 * assistive tech. The look comes from CSS (`:has([data-incomplete])`), which
 * survives a re-render on its own.
 * @param {ParentNode} scope
 */
export function syncAddToCart(scope) {
  const incomplete = Boolean(firstIncomplete(scope));
  for (const button of scope.querySelectorAll('add-to-cart-component button[name="add"]')) {
    if (incomplete) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }
}

/**
 * The Shopify section an element sits in.
 * @param {Element} element
 * @returns {ParentNode}
 */
export function sectionOf(element) {
  return element.closest('.shopify-section, [id^="shopify-section-"]') ?? document.body;
}

/**
 * Restarts a short highlight animation on an element.
 * @param {Element} element
 */
export function nudge(element) {
  element.classList.remove('is-flagged');
  // Force a reflow so the animation restarts on a repeat click.
  void (/** @type {HTMLElement} */ (element).offsetWidth);
  element.classList.add('is-flagged');
  window.setTimeout(() => element.classList.remove('is-flagged'), 1200);
}

/** @param {Element} element */
export function scrollToPick(element) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
}

/**
 * A radio group that needs exactly one answer — empty to start with (solo
 * Standartinis scent) or on a default (solo varnish, Blizgus).
 *
 * Horizon's morph copies each radio's `checked` from the server's markup on
 * every section re-render, so a re-render (a size switch, or a palette
 * variant change on solo kits) would silently put the group back to what the
 * server rendered — none, or the default. The group remembers the customer's
 * pick and puts it back.
 *
 * It notices the morph through `data-picked`: set here after every pick,
 * never rendered by the server, so the morph strips it. `data-incomplete`
 * is watched too — the server renders it on an empty group, so a morph
 * that clears the pick also brings it back.
 */
class CraftlyRequiredGroup extends HTMLElement {
  /** @type {string | null} */
  #value = null;

  /** @type {ParentNode | null} */
  #scope = null;

  #observer = new MutationObserver(() => this.#restore());

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#value = this.#checked()?.value ?? null;
    this.#update();
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-incomplete', 'data-picked'] });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#observer.disconnect();

    const scope = this.#scope;
    if (scope) queueMicrotask(() => syncAddToCart(scope));
  }

  get isComplete() {
    return Boolean(this.#checked());
  }

  /** @returns {HTMLInputElement | null} */
  #checked() {
    return this.querySelector('input[type="radio"]:checked');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;

    this.#value = input.value;
    this.#update();
  };

  #restore() {
    if (this.#value !== null && this.#checked()?.value !== this.#value) {
      const match = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]).find(
        (input) => input.value === this.#value && !input.disabled
      );

      if (match) {
        match.checked = true;
        // The add-on picker rebuilds its payload from the form on `change`.
        match.dispatchEvent(new Event('change', { bubbles: true }));
        return; // `#onChange` runs `#update`.
      }
    }

    this.#update();
  }

  #update() {
    const complete = this.isComplete;

    this.toggleAttribute('data-incomplete', !complete);
    if (complete) this.#error?.setAttribute('hidden', '');

    if (this.#value !== null) this.dataset.picked = this.#value;
    else delete this.dataset.picked;

    this.#scope = sectionOf(this);
    syncAddToCart(this.#scope);

    // That attribute change was ours, not a re-render.
    this.#observer.takeRecords();
  }

  get #error() {
    return this.querySelector('[data-pick-error]');
  }

  flag() {
    scrollToPick(this);
    nudge(this);

    const error = this.#error;
    if (error) {
      error.textContent = this.dataset.requiredMessage ?? '';
      error.removeAttribute('hidden');
    }

    const first = this.querySelector('input[type="radio"]:not(:disabled)');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }
}

/**
 * Registered on `window` in the capture phase: Horizon delegates its
 * `on:click` / `on:submit` handlers from `document` (also capture), and
 * window-capture runs before it, so stopping the event here means the
 * product form never sees it. The sticky add-to-cart bar works by clicking
 * the main button, so it is caught by the same listener.
 */
function interceptAddToCart() {
  /**
   * @param {Event} event
   * @param {Element} origin
   */
  const guard = (event, origin) => {
    const pick = firstIncomplete(sectionOf(origin));
    if (!pick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (typeof pick.flag === 'function') pick.flag();
  };

  window.addEventListener(
    'click',
    (event) => {
      const target = event.composedPath?.()[0] ?? event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('add-to-cart-component button[name="add"]');
      if (button) guard(event, button);
    },
    { capture: true }
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && form.matches('[data-type="add-to-cart-form"]')) guard(event, form);
    },
    { capture: true }
  );
}

if (!customElements.get('craftly-required-group')) {
  customElements.define('craftly-required-group', CraftlyRequiredGroup);
  interceptAddToCart();
}
